import { Component, Input, numberAttribute, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule, NgClass, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../service/auth/auth.service';
import { LoggerService } from '../../service/utils/logger.service';
import { RestService } from '../../service/rest/rest.service';
import { QuestionPoolQuestionDetailComponent } from '../../question-pool-question-detail/question-pool-question-detail.component';

@Component({
  selector: 'app-question-detail-tabs',
  standalone: true,
  imports: [CommonModule, FormsModule, NgClass, NgFor, NgIf, QuestionPoolQuestionDetailComponent],
  templateUrl: './question-detail-tabs.component.html',
  styleUrl: './question-detail-tabs.component.css'
})
export class QuestionDetailTabsComponent implements OnChanges, OnDestroy {
  private _questionId: number = 0;
  private inlineQuestionImageObjectUrls: string[] = [];

  @Input({ required: true, transform: numberAttribute })
  set questionId(value: number) {
    if (this._questionId !== value) {
      // Save notes before switching questions
      this.autoSaveNotes();
      this.revokeInlineQuestionImageUrls();
      this.inlineQuestionImageUrls = [];
      this._questionId = value;
      // Reset indicators
      this.hasNote = false;
      this.hasComments = false;
      // Use initialTab if provided, otherwise default to question tab
      this.activeTab = this.initialTab || 'question';
      // Load tab data for new question and check for content
      this.loadTabData();
      this.checkTabContent(); // Check for notes/comments without loading full data
    }
  }
  
  get questionId(): number {
    return this._questionId;
  }
  
  ngOnChanges(changes: SimpleChanges) {
    // If initialTab is set when modal opens, switch to it
    if (changes['initialTab'] && this.initialTab && this._questionId) {
      if (this.activeTab !== this.initialTab) {
        this.activeTab = this.initialTab;
        this.loadTabData();
      }
    }

    // In modal flows, questionId can be set before questionHtml arrives.
    // When inline HTML appears later, trigger the same internal image pipeline.
    if (changes['questionHtml'] && this._questionId) {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId && this.hasInlineQuestion) {
        this.loadInlineQuestionImages(sessionId);
      }
    }
  }

  // Render the question detail in read-only mode (recommended for modals / result detail views)
  @Input() readOnlyQuestion: boolean = true;

  // Optional: provide question HTML and answers directly (e.g. from search results)
  // so the question tab can render without relying on RestService "current test" context.
  @Input() questionHtml: string | null = null;
  @Input() answersHtml: Array<string | null> = [];
  @Input() correctAnswer: string | null = null;
  @Input() initialTab: 'question' | 'explanation' | 'note' | 'exam' | 'comments' = 'question';

  // Tab management
  activeTab: 'question' | 'explanation' | 'note' | 'exam' | 'comments' = 'question';

  // Explanation tab data
  isLoadingExplanation = false;
  explanationData: { explanationHtml?: string; figureExplanation?: string; explanationImageUrls?: string[] } | null = null;
  inlineQuestionImageUrls: string[] = [];

  // Note tab data
  questionNotes: string = '';
  savedNotes: string = '';
  hasNote: boolean = false;
  private notesSaveTimeout: any = null;

  // Exam state tab data
  isLoadingExamState = false;
  examStateData: { country: string; date: string }[] = [];

  // Comments tab data
  isLoadingComments = false;
  commentsData: { id: number; username: string; comment: string; createdAt: string; userId: number }[] = [];
  hasComments: boolean = false; // Track if comments exist (for tab indicator)
  newComment: string = '';
  isSubmittingComment = false;
  showCommentConfirm = false;
  canSubmitComment = true;
  currentUserId: number | null = null;

  // Flag and hide state
  currentFlagId: number | null = null; // 1=Red, 2=Green, 3=Yellow
  currentHide: number = 0; // 0 or 1
  isLoadingState = false;

  // Image overlay (click explanation image to view full size)
  imageOverlayUrl: string | null = null;

  constructor(
    private restService: RestService,
    private authService: AuthService
  ) {}

  get hasInlineQuestion(): boolean {
    return !!(this.questionHtml && this.questionHtml.trim().length > 0);
  }

  get inlineQuestionHtmlForDisplay(): string {
    const html = this.questionHtml || '';
    // Prevent duplicate rendering when we already show the dedicated image block.
    if (this.inlineQuestionImageUrls.length > 0) {
      return html.replace(/<img[^>]*>/gi, '');
    }
    return html;
  }

  get inlineAnswerRows(): Array<{ idx: number; html: string }> {
    const rows: Array<{ idx: number; html: string }> = [];
    const raw = this.answersHtml || [];
    for (let i = 0; i < raw.length; i++) {
      const v = (raw[i] || '').trim();
      if (v.length > 0) rows.push({ idx: i, html: v });
    }
    return rows;
  }

  answerLetter(idx: number): string {
    const n = Number(idx);
    if (Number.isNaN(n) || n < 0) return '';
    return String.fromCharCode(65 + n);
  }

  isCorrectIdx(idx: number): boolean {
    const expected = (this.correctAnswer || '').trim().toUpperCase();
    if (!expected) return false;
    return this.answerLetter(idx).toUpperCase() === expected;
  }

  ngOnDestroy() {
    try {
      this.autoSaveNotes();
    } catch {
      // ignore
    }
    if (this.notesSaveTimeout) {
      clearTimeout(this.notesSaveTimeout);
    }
    this.revokeInlineQuestionImageUrls();
  }

  private revokeInlineQuestionImageUrls(): void {
    for (const url of this.inlineQuestionImageObjectUrls) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    }
    this.inlineQuestionImageObjectUrls = [];
  }

  private async loadInlineQuestionImages(sessionId: string): Promise<void> {
    this.revokeInlineQuestionImageUrls();
    this.inlineQuestionImageUrls = [];
    try {
      const blobUrls = await this.restService.getQuestionImageBlobUrls(sessionId, this.questionId);
      this.inlineQuestionImageUrls = blobUrls;
      this.inlineQuestionImageObjectUrls = blobUrls.filter((u) => u.startsWith('blob:'));
    } catch (error) {
      LoggerService.error('Error loading inline question images:', error);
    }
  }

  selectTab(tab: 'question' | 'explanation' | 'note' | 'exam' | 'comments') {
    // Save notes before switching tabs
    if (this.activeTab === 'note') {
      this.autoSaveNotes();
    }
    this.activeTab = tab;
    this.loadTabData();
  }

  async loadTabData() {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (!sessionId || !this.questionId) return;

    if (this.hasInlineQuestion) {
      await this.loadInlineQuestionImages(sessionId);
    }

    // Always load question state (flag + hide) when question changes
    await this.loadQuestionState();

    switch (this.activeTab) {
      case 'explanation':
        await this.loadExplanation();
        break;
      case 'note':
        await this.loadNotes();
        break;
      case 'exam':
        await this.loadExamState();
        break;
      case 'comments':
        await this.loadComments();
        break;
    }
  }

  async loadQuestionState() {
    this.isLoadingState = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const state = await this.restService.getQuestionState(sessionId, this.questionId);
        if (state) {
          this.currentFlagId = state.flagId;
          this.currentHide = state.hide || 0;
        } else {
          this.currentFlagId = null;
          this.currentHide = 0;
        }
      }
    } catch (error) {
      LoggerService.error('Error loading question state:', error);
    } finally {
      this.isLoadingState = false;
    }
  }

  async setFlag(flagId: number | null) {
    // Toggle: if clicking the same flag, unset it
    const newFlagId = this.currentFlagId === flagId ? null : flagId;
    await this.saveQuestionState(newFlagId, this.currentHide);
  }

  async toggleHide() {
    const newHide = this.currentHide === 1 ? 0 : 1;
    await this.saveQuestionState(this.currentFlagId, newHide);
  }

  async saveQuestionState(flagId: number | null, hide: number) {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const success = await this.restService.saveQuestionState(sessionId, this.questionId, flagId, hide);
        if (success) {
          this.currentFlagId = flagId;
          this.currentHide = hide;
        }
      }
    } catch (error) {
      LoggerService.error('Error saving question state:', error);
    }
  }

  async loadExplanation() {
    this.isLoadingExplanation = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        this.explanationData = await this.restService.getQuestionExplanation(sessionId, this.questionId);
      }
    } catch (error) {
      LoggerService.error('Error loading explanation:', error);
    } finally {
      this.isLoadingExplanation = false;
    }
  }

  async loadNotes() {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const notes = await this.restService.getQuestionNotes(sessionId, this.questionId);
        this.questionNotes = notes || '';
        this.savedNotes = notes || '';
        this.hasNote = !!(notes && notes.trim().length > 0);
      }
    } catch (error) {
      LoggerService.error('Error loading notes:', error);
    }
  }

  onNotesInput() {
    if (this.notesSaveTimeout) {
      clearTimeout(this.notesSaveTimeout);
    }
    this.hasNote = !!(this.questionNotes && this.questionNotes.trim().length > 0);
  }

  async autoSaveNotes() {
    if (!this.questionId) return;
    if (this.questionNotes === this.savedNotes) return;

    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const success = await this.restService.saveQuestionNotes(sessionId, this.questionId, this.questionNotes);
        if (success) {
          this.savedNotes = this.questionNotes;
          this.hasNote = !!(this.questionNotes && this.questionNotes.trim().length > 0);
        }
      }
    } catch (error) {
      LoggerService.error('Error auto-saving notes:', error);
    }
  }

  async loadExamState() {
    this.isLoadingExamState = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        this.examStateData = await this.restService.getQuestionExamState(sessionId, this.questionId) || [];
      }
    } catch (error) {
      LoggerService.error('Error loading exam state:', error);
    } finally {
      this.isLoadingExamState = false;
    }
  }

  async loadComments() {
    this.isLoadingComments = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        this.commentsData = await this.restService.getQuestionComments(sessionId, this.questionId) || [];
        this.hasComments = this.commentsData.length > 0;
        const userProfile = await this.restService.getUserProfile(sessionId);
        if (userProfile && (userProfile as any).id) {
          this.currentUserId = (userProfile as any).id as number;
        }
        this.updateCanSubmitComment();
      }
    } catch (error) {
      LoggerService.error('Error loading comments:', error);
    } finally {
      this.isLoadingComments = false;
    }
  }

  async checkTabContent() {
    // Check for notes and comments without loading full data (for tab indicators)
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (!sessionId || !this.questionId) return;

    try {
      // Check notes
      const notes = await this.restService.getQuestionNotes(sessionId, this.questionId);
      this.hasNote = !!(notes && notes.trim().length > 0);

      // Check comments (lightweight check - just get count)
      const comments = await this.restService.getQuestionComments(sessionId, this.questionId);
      this.hasComments = !!(comments && comments.length > 0);
      // Also set commentsData if we got it, so the count shows immediately
      if (comments && comments.length > 0) {
        this.commentsData = comments;
      }
    } catch (error) {
      // Silently fail - indicators will update when tabs are actually loaded
      LoggerService.error('Error checking tab content:', error);
    }
  }

  updateCanSubmitComment() {
    if (!this.commentsData || this.commentsData.length === 0) {
      this.canSubmitComment = true;
      return;
    }
    const lastComment = this.commentsData[0];
    this.canSubmitComment = !(this.currentUserId && lastComment.userId === this.currentUserId);
  }

  submitComment() {
    if (!this.newComment || this.newComment.trim().length === 0) return;
    this.showCommentConfirm = true;
  }

  openImageOverlay(url: string) {
    this.imageOverlayUrl = url;
  }

  closeImageOverlay() {
    this.imageOverlayUrl = null;
  }

  cancelComment() {
    this.showCommentConfirm = false;
  }

  async confirmComment() {
    this.showCommentConfirm = false;
    this.isSubmittingComment = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        try {
          const success = await this.restService.submitQuestionComment(sessionId, this.questionId, this.newComment.trim());
          if (success) {
            this.newComment = '';
            await this.loadComments();
          }
        } catch (error: any) {
          if (error?.message) {
            LoggerService.error('Comment submission error:', error.message);
            await this.loadComments();
          } else {
            LoggerService.error('Failed to submit comment', null);
          }
        }
      }
    } catch (error) {
      LoggerService.error('Error submitting comment:', error);
    } finally {
      this.isSubmittingComment = false;
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) + ' ' + date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  }
}

